import React, { useState, useEffect } from 'react';
import { Space, Typography, Spin, Tooltip, Card, Divider } from 'antd';
import { ShareAltOutlined, CopyOutlined, EyeOutlined, LinkOutlined, FileTextOutlined, SyncOutlined, LockOutlined, ExportOutlined } from '@ant-design/icons';
import { useTheme } from '../../contexts/ThemeContext';
import ReportService from '../../services/reportService';
import PortfolioService from '../../services/portfolioService';
// Componentes UI customizados
import Modal from './Modal';
import ConfirmationModal from './ConfirmationModal';
import { Button, TextInput, Alert, Toast } from '../ui';

const { Text, Title } = Typography;

const ShareReportModal = ({ 
  visible, 
  onCancel, 
  reportId,
  portfolioId,
  reportTitle = 'Relatório' 
}) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedPin, setCopiedPin] = useState(false);
  const [renewingPin, setRenewingPin] = useState(false);
  const [renewConfirmationOpen, setRenewConfirmationOpen] = useState(false);
  const [hasRequested, setHasRequested] = useState(false);

  
  useEffect(() => {
  // Só copia automaticamente se o modal estiver aberto e o link foi carregado
  if (visible && shareData?.fullLink) {
    handleCopyLink();
  }
  // eslint-disable-next-line
}, [shareData?.fullLink, visible]);

  // Carregar/criar compartilhamento quando modal abre
  useEffect(() => {
    if (visible && (reportId || portfolioId) && !hasRequested) {
      handleShareReport();
    }
    
    // Reset quando modal fecha
    if (!visible) {
      setHasRequested(false);
      setShareData(null);
      setError(null);
      setCopied(false);
      setCopiedPin(false);
      setRenewingPin(false);
      setLoading(false);
    }
  }, [visible, reportId, portfolioId, hasRequested]);

  const handleShareReport = async () => {
    try {
      console.group('📤 Compartilhando relatório');
      
      setHasRequested(true);
      setLoading(true);
      setError(null);
      let response = null;
      if(reportId) {
        response = await ReportService.shareReport(reportId);
      } else if(portfolioId) {
        response = await PortfolioService.sharePortfolio(portfolioId);
      }
      
      
      if (response?.data) {
        const shareInfo = response.data;
        
        // Gerar link completo
        const fullLink = ReportService.generateFullShareLink(shareInfo.link);
        
        setShareData({
          ...shareInfo,
          fullLink: fullLink
        });
        
        Toast.success('Link de compartilhamento gerado com sucesso!');
      } else {
        throw new Error('Resposta inválida do servidor');
      }
    } catch (err) {
      console.error('❌ Erro ao compartilhar relatório:', err);
      setError(err.response?.data?.message || 'Erro ao gerar link de compartilhamento');
      Toast.error('Erro ao gerar link de compartilhamento');
    } finally {
      setLoading(false);
      console.groupEnd();
    }
  };

  const handleCopyLink = async () => {
    if (!shareData?.fullLink) return;
    try {
      await navigator.clipboard.writeText(shareData.fullLink);
      setCopied(true);
      Toast.success('Link copiado para a área de transferência!');
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      fallbackCopy(shareData.fullLink);
    }
  };

  const handleCopyPin = async () => {
    if (!shareData?.pinCode) return;
    try {
      await navigator.clipboard.writeText(shareData.pinCode);
      setCopiedPin(true);
      Toast.success('PIN copiado!');
      setTimeout(() => setCopiedPin(false), 2000);
    } catch (err) {
      fallbackCopy(shareData.pinCode);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      Toast.success('Copiado!');
    } catch {
      Toast.error('Erro ao copiar');
    }
    document.body.removeChild(textArea);
  };

  const handleRenewPin = () => {
    if (!reportId && !portfolioId) return;
    setRenewConfirmationOpen(true);
  };

  const confirmRenewPin = async () => {
    setRenewingPin(true);
    try {
      let res;
      if (reportId) {
        res = await ReportService.renewPinCode(reportId);
      } else {
        res = await PortfolioService.renewPinCode(portfolioId);
      }
      
      // res.data.pinCode deve ser o novo PIN retornado pela API
      setShareData(prev => ({
        ...prev,
        pinCode: res?.data?.pinCode || prev.pinCode
      }));
      setRenewConfirmationOpen(false);
      Toast.success('Novo PIN gerado!');
    } catch (e) {
      Toast.error('Erro ao gerar novo PIN');
    } finally {
      setRenewingPin(false);
    }
  };


  const handleOpenPreview = () => {
    if (shareData?.fullLink) {
      window.open(shareData.fullLink, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <Modal
        title={
          <Space>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.primary}CC)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 12px ${theme.primary}30`
            }}>
              <ShareAltOutlined style={{ fontSize: 18, color: 'white' }} />
            </div>
            <div>
              <Title level={4} style={{ margin: 0, color: theme.textSecondary }}>
                Compartilhar Relatório
              </Title>
              <Text type="secondary" style={{ fontSize: 13, display: 'block' }}>
                Gere um link público para compartilhar
              </Text>
            </div>
          </Space>
        }
        open={visible}
        onCancel={onCancel}
        footer={null}
        width={650}
        destroyOnHidden
        centered
        variant="info"
      >
        <div style={{ marginTop: 16 }}>
          {loading ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 0',
              background: `${theme.backgroundSecondary}40`,
              borderRadius: 12,
              border: `1px dashed ${theme.borderColor}`
            }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text style={{ color: theme.textSecondary }}>
                  Gerando link de compartilhamento...
                </Text>
              </div>
            </div>
          ) : error ? (
            <Alert
              message="Erro ao Gerar Link"
              description={error}
              type="error"
              showIcon
              style={{ borderRadius: 8, marginBottom: 16 }}
              action={
                <Button size="small" onClick={handleShareReport}>
                  Tentar Novamente
                </Button>
              }
            />
          ) : shareData ? (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* Card do Relatório */}
              <Card 
                style={{ 
                  background: `${theme.backgroundSecondary}60`,
                  border: `1px solid ${theme.borderColor}40`,
                  borderRadius: 12,
                  padding: 8
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ 
                    width: 48, 
                    height: 48, 
                    borderRadius: 10, 
                    background: `linear-gradient(135deg, ${theme.primary}20, ${theme.primary}10)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `2px solid ${theme.primary}30`
                  }}>
                    <FileTextOutlined style={{ fontSize: 20, color: theme.primary }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Title level={5} style={{ margin: 0, color: theme.textSecondary }}>
                      {reportTitle}
                    </Title>
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        <EyeOutlined style={{ marginRight: 4 }} />
                        {shareData.viewsCount || 0} visualizações
                      </Text>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Seção do Link */}
              <div>
                <Text strong style={{ 
                  marginBottom: 12, 
                  display: 'block',
                  color: theme.textSecondary,
                  fontSize: 14
                }}>
                  Link de Compartilhamento
                </Text>
                
                <div style={{ 
                  display: 'flex', 
                  gap: 8,
                  padding: 12,
                  background: `${theme.backgroundSecondary}80`,
                  borderRadius: 10,
                  border: `1px solid ${theme.borderColor}60`
                }}>
                  <TextInput
                    value={shareData.fullLink}
                    readOnly
                    prefixIcon={<LinkOutlined style={{ color: theme.primary }} />}
                    style={{ 
                      flex: 1,
                      background: 'transparent',
                      border: 'none',
                      fontSize: 13,
                      fontFamily: 'monospace'
                    }}
                  />
                  <Tooltip title={copied ? 'Copiado!' : 'Copiar link'}>
                    <Button
                      type={copied ? 'primary' : 'default'}
                      onClick={handleCopyLink}
                      style={{ 
                        minWidth: 80,
                        borderRadius: 6,
                        height: 35,
                        background: copied ? theme.primary : theme.backgroundSecondary,
                        borderColor: copied ? theme.primary : theme.borderColor,
                        color: copied ? 'white' : theme.textSecondary
                      }}
                    >
                      <CopyOutlined style={{ marginRight: 4 }} />
                      {copied ? 'Copiado!' : 'Copiar'}
                    </Button>
                  </Tooltip>
                  <Tooltip title="Abrir em nova aba">
                    <Button
                      onClick={() => {
                        if (shareData?.fullLink) {
                          window.open(shareData.fullLink, '_blank');
                        }
                      }}
                      style={{ 
                        minWidth: 40, // Largura suficiente para o ícone
                        borderRadius: 6,
                        height: 35,
                        background: theme.backgroundSecondary,
                        borderColor: theme.borderColor,
                        color: theme.textSecondary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <ExportOutlined />
                    </Button>
                  </Tooltip>
                </div>
              </div>

              {/* Seção do PIN */}
              {shareData.pinCode && (
                <div>
                  <Text strong style={{
                    marginBottom: 6,
                    marginTop: 16,
                    display: 'block',
                    color: theme.textSecondary,
                    fontSize: 14
                  }}>
                    PIN de acesso ao relatório
                  </Text>
                  <div style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                    padding: 12,
                    background: `${theme.backgroundSecondary}80`,
                    borderRadius: 10,
                    border: `1px solid ${theme.borderColor}60`,
                    marginBottom: 4
                  }}>
                    <Text
                      style={{
                        fontSize: 18,
                        fontFamily: 'monospace',
                        letterSpacing: 4,
                        color: theme.textSecondary,
                        marginRight: 8,
                        marginLeft: 2
                      }}
                    >
                      <LockOutlined style={{ color: theme.primary, marginRight: 7, fontSize: 16 }} />
                      {shareData.pinCode}
                    </Text>
                    <Tooltip title={copiedPin ? 'PIN copiado!' : 'Copiar PIN'}>
                      <Button
                        size="small"
                        style={{
                          borderRadius: 6,
                          color: theme.textSecondary,
                          borderColor: copiedPin ? theme.primary : theme.borderColor,
                          background: copiedPin ? theme.primary : theme.backgroundSecondary
                        }}
                        onClick={handleCopyPin}
                        type={copiedPin ? 'primary' : 'default'}
                      >
                        <CopyOutlined />
                      </Button>
                    </Tooltip>
                    <Tooltip title={renewingPin ? 'Gerando...' : 'Gerar novo PIN'}>
                      <Button
                        size="small"
                        loading={renewingPin}
                        style={{
                          borderRadius: 6,
                          color: theme.textSecondary,
                          borderColor: theme.primary,
                          marginLeft: 4
                        }}
                        onClick={handleRenewPin}
                        type="default"
                      >
                        <SyncOutlined />
                      </Button>
                    </Tooltip>
                  </div>
                  <Text type="secondary" style={{ fontSize: 11, marginLeft: 2 }}>
                    O PIN deve ser informado para acessar o relatório compartilhado.
                  </Text>
                </div>
              )}

              <Divider style={{ margin: '16px 0', borderColor: theme.borderColor }} />

              {/* Ações */}
              <div style={{ 
                display: 'flex', 
                gap: 12, 
                justifyContent: 'flex-end'
              }}>
                <Button 
                  onClick={onCancel}
                  style={{
                    borderRadius: 8,
                    height: 35,
                  }}
                >
                  Fechar
                </Button>
                <Button 
                  type="primary" 
                  onClick={handleOpenPreview}
                  style={{
                    borderRadius: 8,
                    height: 35,
                    background: theme.primary,
                    borderColor: theme.primary,
                  }}
                >
                  <EyeOutlined style={{ marginRight: 4 }} />
                  Visualizar
                </Button>
              </div>
            </Space>
          ) : null}
        </div>
      </Modal>

      <ConfirmationModal
        open={renewConfirmationOpen}
        onCancel={() => setRenewConfirmationOpen(false)}
        onConfirm={confirmRenewPin}
        title="Gerar novo PIN"
        content="Tem certeza que deseja gerar um novo PIN? O PIN atual deixará de funcionar e usuários com o PIN antigo perderão o acesso."
        confirmText="Gerar novo PIN"
        cancelText="Cancelar"
        type="warning"
        loading={renewingPin}
      />
    </>
  );
};

export default ShareReportModal; 